<template>
  <popup
    :close-action="closeActionClicked"
    class="update-credit-card-popup"
    :title="'עדכון פרטי כרטיס'">
    <template v-slot:content>
      <personal-info-form/>
      <credit-card-details-form/>
      <div class="button-container">
        <div class="result-container" v-if="processingCompleted">
          <div class="top">
            <div class="icon">
              <img class="icon" :src="getImageUrl()">
            </div>
            <div class="message" :class="{error: processingCompletedWithError, success: !processingCompletedWithError}">
              {{ resultMessage }}
            </div>
          </div>
          <div class="cta" v-if="processingCompletedWithError">
            אפשר לרענן את העמוד ולנסות להשתמש בכרטיס אחר.
          </div>
        </div>
        <riseup-button v-else class="update-button" :loading="processingInProgress" title="עדכון פרטי כרטיס" :action="updateCreditCardClicked"/>
      </div>
    </template>
  </popup>
</template>

<script>
import BaseUI from 'base-ui';
import { mapActions, mapState } from 'vuex';
import { SUBSCRIPTION_STATES } from '@/constants/subscription-state';
import DDLogs from '@/DDLogs';
import Segment from '../../../../../Segment';
import PersonalInfoForm from './components/PersonalInfoForm.vue';
import CreditCardDetailsForm from './components/CreditCardDetailsForm.vue';
import PaymeInterface from '../../../../../Payme';

const PAYMENT_PROCESSING_STATUSES = {
  NOT_STARTED: 'notStarted',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
};

const PAYMENT_PROCESSING_TIMEOUT = 30000;
const DELAY_BEFORE_POPUP_CLOSE = 2000;

export default {
  name: 'UpdateCreditCardPopup',
  components: {
    RiseupButton: BaseUI.RiseupButton,
    PersonalInfoForm,
    CreditCardDetailsForm,
    Popup: BaseUI.Popup,
  },
  async created() {
    Segment.trackUserInteraction('UpdateCreditCardPopupShown');
  },
  destroyed() {
    PaymeInterface.teardown();
    this.reset();
  },
  data() {
    return {
      processingStatus: PAYMENT_PROCESSING_STATUSES.NOT_STARTED,
    };
  },
  computed: {
    ...mapState('subscription', ['tokenizationError']),
    ...mapState('websocket', ['socketMessage']),
    ...mapState('subscriptionState', ['subscriptionState']),
    resultMessage() {
      return this.processingCompletedWithError ? 'לא הצלחנו לחייב את הכרטיס' : 'פרטי הכרטיס החדש עודכנו בהצלחה';
    },
    processingCompleted() {
      return this.processingStatus === PAYMENT_PROCESSING_STATUSES.COMPLETED;
    },
    processingCompletedWithError() {
      return this.processingCompleted && this.tokenizationError;
    },
    processingInProgress() {
      return this.processingStatus === PAYMENT_PROCESSING_STATUSES.IN_PROGRESS;
    },
  },
  methods: {
    ...mapActions('subscription', ['updateCreditCard', 'reset']),
    ...mapActions('subscriptionState', ['fetchSubscriptionState']),
    ...mapActions('creditCardDetails', ['fetch']),
    closeActionClicked() {
      Segment.trackUserInteraction('UpdateCreditCardPopupClosed');
      this.$emit('close');
    },
    getImageUrl() {
      const iconType = this.processingCompletedWithError ? 'alert' : 'success';
      // eslint-disable-next-line global-require,import/no-dynamic-require
      return require(`@/assets/icons/${iconType}-icon.svg`);
    },
    async updateCreditCardClicked() {
      Segment.trackUserInteraction('UpdateCreditCardPopup_UpdateClicked');
      this.processingStatus = PAYMENT_PROCESSING_STATUSES.IN_PROGRESS;
      DDLogs.log('Updating credit card');
      await this.updateCreditCard();
      if (this.tokenizationError) {
        this.processingStatus = PAYMENT_PROCESSING_STATUSES.COMPLETED;
      }
      this.timeoutProcessing();
    },
    timeoutProcessing() {
      setTimeout(() => {
        if (this.processingInProgress) {
          DDLogs.error('Update credit card process timed out');
          this.closePopupDelayed();
        }
      }, PAYMENT_PROCESSING_TIMEOUT);
    },
    closePopupDelayed() {
      setTimeout(() => {
        this.$emit('close');
      }, DELAY_BEFORE_POPUP_CLOSE);
    },
  },
  watch: {
    async socketMessage(newValue, oldValue) {
      if (this.processingInProgress && newValue !== oldValue) {
        await this.fetchSubscriptionState();
        if (this.processingInProgress && this.subscriptionState === SUBSCRIPTION_STATES.SUBSCRIBED) {
          await this.fetch();
          this.processingStatus = PAYMENT_PROCESSING_STATUSES.COMPLETED;
          this.closePopupDelayed();
        }
      }
    },
  },
};

</script>

<style scoped lang="scss">

  @import "~base-ui/src/scss/riseup-colors";

  .button-container {
    margin-top: 20px;

    .update-button {
      width: 100%;
    }

    .result-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 18px 16px;
      border-radius: 3px;
      background-color: rgba($riseup_gray_0, 0.4);

      .top {
        display: flex;
        flex-direction: row;
        align-items: center;
        font-family: GretaSans;
        font-size: 20px;
        color: $riseup_coral;
        margin-left: 20px;

        .icon {
          margin-left: 11px;
        }

        .message {
          font-size: 20px;
          font-family: GretaSans;

          &.error {
            color: $riseup_coral;
          }

          &.success {
            color: $riseup_green;
          }
        }
      }

      .cta {
        margin-top: 5px;
        font-family: SimplerPro;
        font-size: 16px;
        line-height: 1.5;
      }
    }
  }

</style>
